

import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardFooter from "../components/DashboardFooter";
import { NotificationManager } from "react-notifications";

// Base endpoint. List = GET base. Create = POST base. Update = PUT base + `${id}/`.
const API_URL = `${process.env.REACT_APP_HOST_API_URL}/admin-user/get-admin-poromotios/`;

const DISCOUNT_TYPE_OPTIONS = [
  { value: "amount", label: "Flat Amount" },
  { value: "percentage", label: "Percentage" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  title: "",
  description: "",
  promo_code: "",
  status: true,
  discount_type: "amount",
  discount_amount: "",
  discount_percentage: "",
  min_purchase_amount: "",
  per_user_count: "1",
  end_date: "",
  imageFile: null,
  imagePreview: null,
});

// ---------- small inline icons (no extra dependency) ----------
const Icon = {
  Tag: (props) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20.59 13.41L11 3.83A2 2 0 009.59 3H4a1 1 0 00-1 1v5.59a2 2 0 00.59 1.41l9.58 9.59a2 2 0 002.83 0l4.59-4.59a2 2 0 000-2.59z" />
      <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  ),
  Shield: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Clock: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  ),
  Percent: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 5L5 19" />
      <circle cx="6.5" cy="6.5" r="2.2" />
      <circle cx="17.5" cy="17.5" r="2.2" />
    </svg>
  ),
  Search: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  ),
  Upload: (props) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
    </svg>
  ),
  Pencil: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  Dots: (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  ),
  X: (props) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
};

const ManagePromotions = () => {
  const token = localStorage.getItem("token");

  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [originalForm, setOriginalForm] = useState(null); // snapshot for diffing on edit
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [imageDragOver, setImageDragOver] = useState(false);
  const imageInputRef = useRef(null);

  const authHeaders = token ? { Authorization: "Bearer " + token } : {};

  // ---------- Fetch promotions ----------
  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: { ...authHeaders },
      });
      if (!res.ok) throw new Error("Failed to fetch promotions");
      const data = await res.json();
      setPromotions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error fetching promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Derived: display helpers per promotion ----------
  const parsedPromotions = useMemo(
    () =>
      promotions.map((p) => {
        const isExpired = p.end_date ? new Date(p.end_date) < new Date(todayISO()) : false;
        const usesPercentage = Number(p.discount_percentage) > 0;
        return {
          ...p,
          _discountLabel: usesPercentage
            ? `${p.discount_percentage}% off`
            : `$${p.discount_amount || 0} off`,
          _isExpired: isExpired,
          _isPercentage: usesPercentage,
        };
      }),
    [promotions]
  );

  const filteredPromotions = useMemo(() => {
    return parsedPromotions.filter((p) => {
      const matchesSearch =
        !search ||
        (p.title || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.promo_code || "").toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" && p.status) || (statusFilter === "inactive" && !p.status);
      const matchesType =
        typeFilter === "all" ||
        (typeFilter === "percentage" && p._isPercentage) ||
        (typeFilter === "amount" && !p._isPercentage);
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [parsedPromotions, search, statusFilter, typeFilter]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = promotions.length;
    const active = promotions.filter((p) => p.status).length;
    const expired = parsedPromotions.filter((p) => p._isExpired).length;
    const percentageBased = parsedPromotions.filter((p) => p._isPercentage).length;
    return { total, active, expired, percentageBased };
  }, [promotions, parsedPromotions]);

  // ---------- Modal open/close ----------
  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOriginalForm(null);
    setShowModal(true);
  };

  const openEditForm = (promo) => {
    const usesPercentage = Number(promo.discount_percentage) > 0;
    const initial = {
      ...emptyForm(),
      title: promo.title || "",
      description: promo.description || "",
      promo_code: promo.promo_code || "",
      status: !!promo.status,
      discount_type: usesPercentage ? "percentage" : "amount",
      discount_amount: promo.discount_amount ?? "",
      discount_percentage: promo.discount_percentage ?? "",
      min_purchase_amount: promo.min_purchase_amount ?? "",
      per_user_count: promo.per_user_count ?? "1",
      end_date: promo.end_date ? String(promo.end_date).slice(0, 10) : "",
      imagePreview: promo.image_url || promo.image || null,
    };
    setEditingId(promo.id);
    setForm(initial);
    setOriginalForm(initial); // snapshot to diff against on submit
    setOpenMenuId(null);
    setShowModal(true);
  };

  const closeForm = () => {
    setShowModal(false);
    setEditingId(null);
    setOriginalForm(null);
  };

  // ---------- Field helpers ----------
  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const formErrors = useMemo(() => {
    const errs = [];
    if (!form.title.trim()) errs.push("Title is required");
    if (!form.promo_code.trim()) errs.push("Promo code is required");
    if (!form.end_date) errs.push("End date is required");
    if (form.discount_type === "amount" && !(Number(form.discount_amount) > 0)) errs.push("Discount amount must be greater than 0");
    if (form.discount_type === "percentage" && !(Number(form.discount_percentage) > 0)) errs.push("Discount percentage must be greater than 0");
    return errs;
  }, [form]);

  // ---------- Image handlers ----------
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };

  const removeImageFile = () => setForm((f) => ({ ...f, imageFile: null, imagePreview: null }));

  const handleImageDrop = (e) => {
    e.preventDefault();
    setImageDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };

  // ---------- Submit (create or update) ----------
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formErrors.length) {
      NotificationManager.error(formErrors[0]);
      return;
    }

    setSaving(true);

    // FormData can't carry a real `null`, so a field the user cleared is sent as an
    // empty string; the backend should coerce "" -> null for those columns.
    const orEmpty = (v) => (v === null || v === undefined ? "" : v);

    try {
      if (editingId) {
        // ---- UPDATE: PUT only the fields that changed ----
        const changed = {};

        const maybeAdd = (key, currentVal, originalVal) => {
          if (String(currentVal ?? "") !== String(originalVal ?? "")) {
            changed[key] = currentVal;
          }
        };

        maybeAdd("title", form.title, originalForm.title);
        maybeAdd("description", orEmpty(form.description), originalForm.description);
        maybeAdd("promo_code", form.promo_code.toUpperCase(), originalForm.promo_code);
        maybeAdd("status", form.status ? "True" : "False", originalForm.status ? "True" : "False");
        maybeAdd("per_user_count", orEmpty(form.per_user_count), originalForm.per_user_count);
        maybeAdd("min_purchase_amount", orEmpty(form.min_purchase_amount), originalForm.min_purchase_amount);
        maybeAdd("end_date", form.end_date, originalForm.end_date);
console.log("Changed fields:", changed);
        // Discount fields: only send the active type if it changed. If the discount
        // type itself was switched, the newly-inactive type is cleared to empty.
        if (form.discount_type !== originalForm.discount_type) {
          changed[form.discount_type === "amount" ? "discount_percentage" : "discount_amount"] = "";
          maybeAdd(
            form.discount_type === "amount" ? "discount_amount" : "discount_percentage",
            orEmpty(form.discount_type === "amount" ? form.discount_amount : form.discount_percentage),
            "" // force-include since the type itself changed
          );
        } else if (form.discount_type === "amount") {
          maybeAdd("discount_amount", orEmpty(form.discount_amount), originalForm.discount_amount);
        } else {
          maybeAdd("discount_percentage", orEmpty(form.discount_percentage), originalForm.discount_percentage);
        }

        const url = `${API_URL}${editingId}/`;
        const fd = new FormData();
        Object.entries(changed).forEach(([k, v]) => fd.append(k, v));
        if (form.imageFile) fd.append("image", form.imageFile);

        if (Object.keys(changed).length === 0 && !form.imageFile) {
          NotificationManager.info("No changes to update");
          setSaving(false);
          return;
        }

        const res = await fetch(url, {
          method: "PUT",
          headers: { ...authHeaders }, // do NOT set Content-Type — browser sets the multipart boundary itself
          body: fd,
        });

        const data = await res.json();
        console.log("Update response:", data);
        if (!res.ok) throw new Error(data.error || "Request failed");

        NotificationManager.success("Promotion updated successfully");
      } else {
        // ---- CREATE: send the full payload ----
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", orEmpty(form.description));
        fd.append("promo_code", form.promo_code.toUpperCase());
        fd.append("status", form.status ? "True" : "False");
        fd.append("per_user_count", orEmpty(form.per_user_count));
        fd.append("min_purchase_amount", orEmpty(form.min_purchase_amount));
        fd.append("end_date", form.end_date);
        fd.append("discount_amount", form.discount_type === "amount" ? orEmpty(form.discount_amount) : "");
        fd.append("discount_percentage", form.discount_type === "percentage" ? orEmpty(form.discount_percentage) : "");
        if (form.imageFile) fd.append("image", form.imageFile);

        const res = await fetch(API_URL, {
          method: "POST",
          headers: { ...authHeaders },
          body: fd,
        });

        const data = await res.json();
        console.log("Create response:", data);
        if (!res.ok) throw new Error(data.error || "Request failed");

        NotificationManager.success("Promotion created successfully");
      }

      closeForm();
      fetchPromotions();
    } catch (err) {
      console.error(err);
      NotificationManager.error(err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 pb-2 px-3" onClick={() => openMenuId && setOpenMenuId(null)}>
      <style>{`
        .mpr-header {
          position: relative;
          overflow: hidden;
          background: linear-gradient(120deg, #0a5fc4 0%, #1e88e8 55%, #4fb3f0 100%);
          border-radius: 18px;
        }
        .mpr-header::before, .mpr-header::after {
          content: "";
          position: absolute;
          border-radius: 50%;
          background: rgba(255,255,255,.10);
        }
        .mpr-header::before { width: 220px; height: 220px; top: -110px; right: 60px; }
        .mpr-header::after { width: 90px; height: 90px; top: 20px; right: 230px; background: rgba(255,255,255,.14); }
        .mpr-header-inner { position: relative; z-index: 1; }
        .mpr-header-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(255,255,255,.18);
          display: flex; align-items: center; justify-content: center;
          color: #fff;
        }
        .mpr-stat-card {
          background: rgba(255,255,255,.14);
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 14px;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          height: 100%;
        }
        .mpr-stat-icon {
          width: 40px; height: 40px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          background: #fff;
        }
        .mpr-stat-icon.blue { color: #0a5fc4; }
        .mpr-stat-icon.green { background: #eafaf0; color: #16a34a; }
        .mpr-stat-icon.amber { background: #ffb443; color: #7a4a00; }
        .mpr-stat-icon.indigo { background: #fff; color: #2a5cd8; }
        .mpr-stat-value { font-size: 22px; font-weight: 700; color: #fff; line-height: 1.1; }
        .mpr-stat-label { font-size: 12.5px; color: rgba(255,255,255,.85); font-weight: 500; }
        .mpr-stat-sub { font-size: 11px; color: rgba(255,255,255,.65); }

        .mpr-filter-bar {
          background: #fff;
          border: 1px solid #eef1f5;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(16,24,40,.05);
          padding: 14px 16px;
        }
        .mpr-search-wrap { position: relative; }
        .mpr-search-wrap .mpr-search-icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #98a2b3;
        }
        .mpr-search-wrap input {
          padding-left: 38px; border-radius: 10px; border: 1px solid #e4e7ec;
        }
        .mpr-filter-select {
          border-radius: 10px; border: 1px solid #e4e7ec; font-size: 14px;
        }
        .mpr-add-btn {
          border-radius: 10px; font-weight: 600; background: #0a5fc4; border: none;
          white-space: nowrap;
        }
        .mpr-add-btn:hover { background: #084c9e; }

        .mpr-card {
          background: #fff;
          border: 1px solid #edf0f4;
          border-left: 4px solid #0a5fc4;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(16,24,40,.05);
          transition: box-shadow .15s ease, transform .15s ease;
          height: 100%;
        }
        .mpr-card:hover { box-shadow: 0 10px 24px rgba(16,24,40,.10); transform: translateY(-2px); }
        .mpr-card.expired { border-left-color: #d0d5dd; opacity: .82; }
        .mpr-card-img {
          width: 100%;
          height: 130px;
          border-radius: 10px;
          overflow: hidden;
          background: #f3f5f8;
          display: flex; align-items: center; justify-content: center;
        }
        .mpr-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .mpr-card-noimg { font-size: 11px; color: #98a2b3; }
        .mpr-card-title { font-size: 15px; font-weight: 700; color: #101828; }
        .mpr-card-desc {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .mpr-badge {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 600;
          padding: 3px 10px; border-radius: 999px; white-space: nowrap;
        }
        .mpr-badge-code { background: #eef2ff; color: #4338ca; font-family: monospace; letter-spacing: .3px; }
        .mpr-badge-percentage { background: #fff4e0; color: #b7791f; }
        .mpr-badge-amount { background: #eef4ff; color: #2563eb; }
        .mpr-badge-expired { background: #f2f4f7; color: #667085; }
        .mpr-badge-success { color: #16a34a; }
        .mpr-badge-danger { color: #dc2626; }
        .mpr-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
        .mpr-dot.success { background: #16a34a; }
        .mpr-dot.danger { background: #dc2626; }

        .mpr-discount { color: #0a5fc4; font-weight: 700; font-size: 15px; }

        .mpr-btn-edit {
          border-radius: 8px;
          border: 1px solid #0a5fc4;
          color: #0a5fc4;
          font-weight: 600;
          background: #fff;
          font-size: 13px;
        }
        .mpr-btn-edit:hover { background: #0a5fc4; color: #fff; }

        .mpr-kebab {
          width: 30px; height: 30px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #e4e7ec; background: #fff; color: #667085;
          position: relative;
        }
        .mpr-kebab-menu {
          position: absolute; right: 0; top: 36px; z-index: 10;
          background: #fff; border: 1px solid #eef1f5; border-radius: 10px;
          box-shadow: 0 8px 20px rgba(16,24,40,.12);
          min-width: 130px; padding: 6px;
        }
        .mpr-kebab-menu button {
          width: 100%; text-align: left; border: none; background: none;
          padding: 8px 10px; border-radius: 6px; font-size: 13px; color: #344054;
        }
        .mpr-kebab-menu button:hover { background: #f2f4f7; }

        .mpr-modal-overlay { background: rgba(16,24,40,.55); backdrop-filter: blur(2px); }
        .mpr-modal { border-radius: 16px; overflow: hidden; }
        .mpr-modal-header {
          background: linear-gradient(120deg, #0a5fc4 0%, #1e88e8 100%);
          color: #fff;
        }
        .mpr-modal-close {
          width: 30px; height: 30px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,.5);
          background: transparent; color: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .mpr-modal-close:hover { background: rgba(255,255,255,.15); }

        .mpr-dropzone {
          border: 1.5px dashed #cdd5e0;
          border-radius: 12px;
          padding: 22px 12px;
          text-align: center;
          color: #667085;
          cursor: pointer;
          transition: border-color .15s ease, background .15s ease;
          background: #fbfcfe;
        }
        .mpr-dropzone:hover, .mpr-dropzone.drag-over { border-color: #0a5fc4; background: #f0f7ff; }
        .mpr-dropzone small { display: block; color: #98a2b3; margin-top: 2px; }

        .mpr-thumb { position: relative; }
        .mpr-thumb img {
          width: 90px; height: 90px; object-fit: cover; border-radius: 8px; border: 1px solid #e4e7ec;
        }
        .mpr-thumb .mpr-thumb-x {
          position: absolute; top: -6px; right: -6px;
          width: 20px; height: 20px; border-radius: 50%;
          background: #e11d48; color: #fff; border: 2px solid #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
        }

        .mpr-section-label { font-size: 13.5px; font-weight: 600; color: #344054; margin-bottom: 6px; }

        .mpr-type-toggle {
          display: flex; border: 1px solid #e4e7ec; border-radius: 10px; overflow: hidden;
        }
        .mpr-type-toggle button {
          flex: 1; border: none; background: #fff; padding: 8px 10px; font-size: 13px; font-weight: 600; color: #667085;
        }
        .mpr-type-toggle button.active { background: #0a5fc4; color: #fff; }

        .mpr-errors {
          background: #fef3f2; color: #b42318; border: 1px solid #fda29b;
          border-radius: 8px; padding: 8px 12px; font-size: 12.5px;
        }
      `}</style>

      <div className="flex-grow-1">
        <div className="container-fluid pb-2 px-2 px-md-4" style={{ paddingTop: "20px" }}>
          {/* Header */}
          <div className="mpr-header mb-4 p-4">
            <div className="mpr-header-inner">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="mpr-header-icon">
                  <Icon.Tag />
                </div>
                <div>
                  <h3 className="text-white mb-1 fw-bold">Manage Promotions</h3>
                  <p className="text-white mb-0 opacity-75">Create, edit and manage all your promo offers</p>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <div className="mpr-stat-card">
                    <div className="mpr-stat-icon blue">
                      <Icon.Tag />
                    </div>
                    <div>
                      <div className="mpr-stat-label">Total Promotions</div>
                      <div className="mpr-stat-value">{stats.total}</div>
                      <div className="mpr-stat-sub">All promotions</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mpr-stat-card">
                    <div className="mpr-stat-icon green">
                      <Icon.Shield />
                    </div>
                    <div>
                      <div className="mpr-stat-label">Active Promotions</div>
                      <div className="mpr-stat-value">{stats.active}</div>
                      <div className="mpr-stat-sub">Currently active</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mpr-stat-card">
                    <div className="mpr-stat-icon amber">
                      <Icon.Clock />
                    </div>
                    <div>
                      <div className="mpr-stat-label">Expired</div>
                      <div className="mpr-stat-value">{stats.expired}</div>
                      <div className="mpr-stat-sub">Past end date</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="mpr-stat-card">
                    <div className="mpr-stat-icon indigo">
                      <Icon.Percent />
                    </div>
                    <div>
                      <div className="mpr-stat-label">Percentage-based</div>
                      <div className="mpr-stat-value">{stats.percentageBased}</div>
                      <div className="mpr-stat-sub">% discount offers</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mpr-filter-bar row g-2 align-items-center mb-4">
            <div className="col-12 col-md-6">
              <div className="mpr-search-wrap">
                <span className="mpr-search-icon">
                  <Icon.Search />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by title or promo code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select mpr-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select mpr-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">All Discount Types</option>
                <option value="amount">Flat Amount</option>
                <option value="percentage">Percentage</option>
              </select>
            </div>
            <div className="col-6 col-md-1">
              <button className="btn btn-primary mpr-add-btn w-100 py-2" onClick={openAddForm}>
                + Add
              </button>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center py-5 text-muted">Loading promotions...</div>
          ) : filteredPromotions.length === 0 ? (
            <div className="text-center py-5 text-muted">No promotions found.</div>
          ) : (
            <div className="row g-3">
              {filteredPromotions.map((promo) => (
                <div className="col-12 col-md-6 col-lg-4" key={promo.id}>
                  <div className={`mpr-card p-3 d-flex flex-column ${promo._isExpired ? "expired" : ""}`}>
                    <div className="d-flex gap-3 mb-2">
                      <div className="mpr-card-img flex-shrink-0" style={{ width: 90, height: 90 }}>
                        {promo.image_url || promo.image ? (
                          <img src={promo.image_url || promo.image} alt={promo.title} />
                        ) : (
                          <span className="mpr-card-noimg">No image</span>
                        )}
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="mpr-card-title mb-1">{promo.title || "Untitled promotion"}</div>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                          <span className="mpr-badge mpr-badge-code">{promo.promo_code}</span>
                          <span className={`mpr-badge ${promo._isPercentage ? "mpr-badge-percentage" : "mpr-badge-amount"}`}>
                            {promo._isPercentage ? "% Off" : "Flat Off"}
                          </span>
                          {promo._isExpired && <span className="mpr-badge mpr-badge-expired">Expired</span>}
                        </div>
                        <div className="mpr-discount">{promo._discountLabel}</div>
                      </div>
                    </div>

                    <p className="text-muted small mb-2 mpr-card-desc" style={{ minHeight: 30 }}>
                      {promo.description || "No description provided."}
                    </p>

                    <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
                      {promo.min_purchase_amount ? <span>Min ${promo.min_purchase_amount}</span> : null}
                      {promo.per_user_count ? <span>{promo.per_user_count}/user</span> : null}
                      {promo.end_date && <span>Ends {String(promo.end_date).slice(0, 10)}</span>}
                    </div>

                    <hr className="my-2" />

                    <div className="d-flex align-items-center justify-content-between">
                      <span className="d-inline-flex align-items-center gap-2 small fw-semibold">
                        <span className={`mpr-dot ${promo.status ? "success" : "danger"}`}></span>
                        <span className={promo.status ? "mpr-badge-success" : "mpr-badge-danger"}>{promo.status ? "Active" : "Inactive"}</span>
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-sm mpr-btn-edit px-3 d-inline-flex align-items-center gap-1" onClick={() => openEditForm(promo)}>
                          <Icon.Pencil /> Edit
                        </button>
                        <div className="mpr-kebab" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === promo.id ? null : promo.id); }}>
                          <Icon.Dots />
                          {openMenuId === promo.id && (
                            <div className="mpr-kebab-menu" onClick={(e) => e.stopPropagation()}>
                              <button type="button" onClick={() => openEditForm(promo)}>
                                Edit promotion
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- Modal ---------------- */}
      {showModal && (
        <div
          className="mpr-modal-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 2000 }}
          onClick={(e) => e.target === e.currentTarget && closeForm()}
        >
          <div className="mpr-modal bg-white shadow-lg" style={{ width: "94%", maxWidth: "760px", maxHeight: "92vh", overflowY: "auto" }}>
            <form onSubmit={handleSubmit}>
              <div className="mpr-modal-header d-flex align-items-center justify-content-between px-4 py-3">
                <h5 className="mb-0 fw-bold">{editingId ? "Edit Promotion" : "Add Promotion"}</h5>
                <button type="button" className="mpr-modal-close" onClick={closeForm}>
                  <Icon.X />
                </button>
              </div>

              <div className="px-4 py-4">
                <div className="row g-3">
                  <div className="col-12 col-md-8">
                    <label className="form-label mpr-section-label">Title *</label>
                    <input
                      type="text"
                      className="form-control mpr-filter-select"
                      value={form.title}
                      onChange={(e) => updateField("title", e.target.value)}
                      placeholder=""
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label mpr-section-label">Promo Code *</label>
                    <input
                      type="text"
                      className="form-control mpr-filter-select text-uppercase"
                      value={form.promo_code}
                      onChange={(e) => updateField("promo_code", e.target.value)}
                      placeholder=""
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label mpr-section-label">Description</label>
                    <textarea
                      className="form-control mpr-filter-select"
                      rows={3}
                      value={form.description}
                      onChange={(e) => updateField("description", e.target.value)}
                      placeholder=""
                    />
                  </div>

                  <div className="col-12 col-md-5">
                    <label className="form-label mpr-section-label">Discount Type *</label>
                    <div className="mpr-type-toggle">
                      {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={form.discount_type === opt.value ? "active" : ""}
                          onClick={() => updateField("discount_type", opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label mpr-section-label">
                      {form.discount_type === "percentage" ? "Discount %" : "Discount $"} *
                    </label>
                    {form.discount_type === "percentage" ? (
                      <input
                        type="number"
                        className="form-control mpr-filter-select"
                        value={form.discount_percentage}
                        onChange={(e) => updateField("discount_percentage", e.target.value)}
                        placeholder=""
                      />
                    ) : (
                      <input
                        type="number"
                        min="0"
                        className="form-control mpr-filter-select"
                        value={form.discount_amount}
                        onChange={(e) => updateField("discount_amount", e.target.value)}
                        placeholder=""
                      />
                    )}
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label mpr-section-label">Status *</label>
                    <select
                      className="form-select mpr-filter-select"
                      value={form.status ? "active" : "inactive"}
                      onChange={(e) => updateField("status", e.target.value === "active")}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="col-6 col-md-4">
                    <label className="form-label mpr-section-label">Min Purchase ($)</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control mpr-filter-select"
                      value={form.min_purchase_amount}
                      onChange={(e) => updateField("min_purchase_amount", e.target.value)}
                      placeholder=""
                    />
                  </div>
                  <div className="col-6 col-md-4">
                    <label className="form-label mpr-section-label">Uses Per User</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control mpr-filter-select"
                      value={form.per_user_count}
                      onChange={(e) => updateField("per_user_count", e.target.value)}
                      placeholder=""
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label mpr-section-label">End Date *</label>
                    <input
                      type="date"
                      className="form-control mpr-filter-select"
                      value={form.end_date}
                      min={todayISO()}
                      onChange={(e) => updateField("end_date", e.target.value)}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label mpr-section-label">Promotion Image</label>
                    <div className="d-flex gap-2 align-items-start">
                      <div
                        className={`mpr-dropzone flex-grow-1 ${imageDragOver ? "drag-over" : ""}`}
                        onClick={() => imageInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
                        onDragLeave={() => setImageDragOver(false)}
                        onDrop={handleImageDrop}
                      >
                        <Icon.Upload />
                        <div className="mt-1 small fw-semibold">Drag &amp; drop or click to upload</div>
                        <small>PNG, JPG, JPEG (Max 2MB)</small>
                        <input ref={imageInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageChange} />
                      </div>
                      {form.imagePreview && (
                        <div className="mpr-thumb flex-shrink-0">
                          <img src={form.imagePreview} alt="promotion preview" />
                          <span className="mpr-thumb-x" onClick={removeImageFile}>
                            <Icon.X />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {formErrors.length > 0 && (
                    <div className="col-12">
                      <div className="mpr-errors">⚠ {formErrors[0]}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 border-top px-4 py-3">
                <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary mpr-add-btn px-4" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default ManagePromotions;